import { SpecialDateCategories } from "../../../../pojo/SpecialDate";
import { S25Util } from "../../../../util/s25-util";
import { RowData } from "../../../s25-spreadsheet/types/sheet.js.types.expanded";
import { SpecialDateImportRow } from "../../import.service";
import { ImportUtil } from "../import.util";

type MappedField = "name" | "category" | "startDate" | "endDate" | "showOnCalendar" | "active" | "seedKey";

const FIELD_ALIASES: ReadonlyArray<readonly [MappedField, readonly string[]]> = [
    ["name", ["name"]],
    ["category", ["type", "category", "category_code", "category code"]],
    ["startDate", ["start_date", "start date"]],
    ["endDate", ["end_date", "end date"]],
    ["showOnCalendar", ["show_on_calendar", "show on calendar", "showoncalendar"]],
    ["active", ["active", "is_active", "is active"]],
    ["seedKey", ["seed_key", "seed key"]],
];

const CATEGORY_LABELS: ReadonlyArray<readonly [string, readonly string[]]> = [
    [SpecialDateCategories.HOLIDAY, ["holiday"]],
    [SpecialDateCategories.CLOSURE, ["closure"]],
    [SpecialDateCategories.ACADEMIC, ["academic"]],
];

export class SpecialDateSpreadsheetUtil {
    static requiredColumns(): string[] {
        return ["Name", "Type", "Start Date"];
    }

    static templateRow(): Record<string, string> {
        return {
            Name: "Spring break",
            Type: "academic",
            "Start Date": "2026-03-16",
            "End Date": "2026-03-20",
            "Show on Calendar": "true",
        };
    }

    static rowsToSpecialDates(rows: RowData[], errorSet: Set<string>): SpecialDateImportRow[] {
        if (!rows?.length) {
            return [];
        }
        const missing = ImportUtil.getMissingRequiredColumns(headerMap(rows[0]), this.requiredColumns());
        if (missing.length) {
            errorSet.add(`Missing required column(s): ${missing.join(", ")}`);
            return [];
        }

        const fieldByCol = mapHeaderFields(rows[0]);
        const parsed: SpecialDateImportRow[] = [];
        const seenKeys = new Set<string>();

        for (let rowIndex = 1; rowIndex < rows.length; rowIndex++) {
            const rowNumber = rowIndex + 1;
            const importRow = this.rowToSpecialDate(rows[rowIndex], fieldByCol, errorSet, rowNumber);
            if (!importRow) {
                continue;
            }
            const duplicateKey = naturalKey(importRow.name, importRow.startDate);
            if (seenKeys.has(duplicateKey)) {
                errorSet.add(`Row ${rowNumber}: Duplicate special date "${importRow.name}" on ${importRow.startDate}.`);
                continue;
            }
            seenKeys.add(duplicateKey);
            parsed.push(importRow);
        }
        return parsed;
    }

    private static rowToSpecialDate(
        row: RowData,
        fieldByCol: Map<number, MappedField>,
        errorSet: Set<string>,
        rowNumber: number,
    ): SpecialDateImportRow | undefined {
        if (!row?.cells || !row.cells.some((cell) => cell?.text != null && String(cell.text).trim() !== "")) {
            return undefined;
        }

        const importRow: SpecialDateImportRow = {
            name: "",
            categoryCode: "",
            startDate: "",
            endDate: "",
        };

        fieldByCol.forEach((field, col) => {
            const raw = row.cells?.[col]?.text;
            if (raw == null || String(raw).trim() === "") {
                return;
            }
            const text = String(raw).trim();
            switch (field) {
                case "name":
                    importRow.name = text;
                    break;
                case "category":
                    importRow.categoryCode = parseCategory(text, rowNumber, errorSet) ?? "";
                    break;
                case "startDate":
                    importRow.startDate = parseCivilDateCell(raw, rowNumber, errorSet, "Start Date") ?? "";
                    break;
                case "endDate":
                    importRow.endDate = parseCivilDateCell(raw, rowNumber, errorSet, "End Date") ?? "";
                    break;
                case "showOnCalendar":
                    importRow.showOnCalendar = parseBool(text);
                    break;
                case "active":
                    importRow.active = parseBool(text);
                    break;
                case "seedKey":
                    importRow.seedKey = text;
                    break;
                default:
                    break;
            }
        });

        if (!importRow.name.trim()) {
            errorSet.add(`Row ${rowNumber}: Name is required.`);
            return undefined;
        }
        if (!importRow.categoryCode) {
            if (!errorSetHasRowCategoryError(errorSet, rowNumber)) {
                errorSet.add(`Row ${rowNumber}: Type is required (holiday, closure, or academic).`);
            }
            return undefined;
        }
        if (!importRow.startDate) {
            return undefined;
        }
        if (!importRow.endDate) {
            importRow.endDate = civilToImportEndDateTime(importRow.startDate);
        } else {
            importRow.endDate = civilToImportEndDateTime(importRow.endDate);
        }
        importRow.startDate = civilToImportStartDateTime(importRow.startDate);
        if (importRow.endDate < importRow.startDate) {
            errorSet.add(`Row ${rowNumber}: End date must be on or after start date.`);
            return undefined;
        }
        if (importRow.showOnCalendar == null) {
            importRow.showOnCalendar = true;
        }
        if (importRow.active == null) {
            importRow.active = true;
        }
        return importRow;
    }
}

function headerMap(headerRow: RowData): Map<number, string> {
    const headers = new Map<number, string>();
    headerRow?.cells?.forEach((cell, col) => {
        if (cell?.text != null) {
            headers.set(col, String(cell.text));
        }
    });
    return headers;
}

function mapHeaderFields(headerRow: RowData): Map<number, MappedField> {
    const fieldByCol = new Map<number, MappedField>();
    headerRow?.cells?.forEach((cell, col) => {
        if (cell?.text == null) {
            return;
        }
        const normalized = ImportUtil.normalize(String(cell.text));
        for (const [field, aliases] of FIELD_ALIASES) {
            if (aliases.some((alias) => ImportUtil.normalize(alias) === normalized)) {
                fieldByCol.set(col, field);
                return;
            }
        }
    });
    return fieldByCol;
}

function parseCategory(value: string, rowNumber: number, errorSet: Set<string>): string | undefined {
    const normalized = ImportUtil.normalize(value);
    for (const [code, aliases] of CATEGORY_LABELS) {
        if (aliases.some((alias) => ImportUtil.normalize(alias) === normalized)) {
            return code;
        }
    }
    errorSet.add(`Row ${rowNumber}: Invalid type "${value}". Use holiday, closure, or academic.`);
    return undefined;
}

function parseBool(value: string): boolean {
    const lowered = value.trim().toLowerCase();
    return lowered === "t" || lowered === "true" || lowered === "y" || lowered === "yes" || lowered === "1";
}

function parseCivilDateCell(
    raw: unknown,
    rowNumber: number,
    errorSet: Set<string>,
    fieldLabel: string,
): string | undefined {
    if (raw == null || String(raw).trim() === "") {
        errorSet.add(`Row ${rowNumber}: ${fieldLabel} is required.`);
        return undefined;
    }

    if (typeof raw === "number" || (typeof raw === "string" && S25Util.isNumeric(raw.trim()))) {
        const serial = typeof raw === "number" ? raw : S25Util.parseFloat(raw.trim());
        const fromSerial = excelSerialToCivil(serial);
        if (fromSerial) {
            return fromSerial;
        }
        errorSet.add(`Row ${rowNumber}: Invalid ${fieldLabel} serial value "${raw}".`);
        return undefined;
    }

    const text = String(raw).trim();
    if (isAmbiguousSlashDate(text)) {
        errorSet.add(`Row ${rowNumber}: Ambiguous ${fieldLabel} "${text}". Use YYYY-MM-DD (for example 2026-03-16).`);
        return undefined;
    }

    const civil = S25Util.date.toCivilDateString(text);
    if (civil && /^\d{4}-\d{2}-\d{2}$/.test(civil)) {
        return civil;
    }

    errorSet.add(`Row ${rowNumber}: Invalid ${fieldLabel} "${text}". Use YYYY-MM-DD.`);
    return undefined;
}

function isAmbiguousSlashDate(text: string): boolean {
    const slashMatch = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!slashMatch) {
        return false;
    }
    const month = S25Util.parseInt(slashMatch[1]);
    const day = S25Util.parseInt(slashMatch[2]);
    if (month == null || day == null) {
        return false;
    }
    return month <= 12 && day <= 12 && month !== day;
}

function excelSerialToCivil(serial: number): string | undefined {
    const xlsx = (
        window as Window & {
            XLSX?: { SSF?: { parse_date_code?: (code: number) => { y: number; m: number; d: number } } };
        }
    ).XLSX;
    const parsed = xlsx?.SSF?.parse_date_code?.(serial);
    if (!parsed?.y || !parsed?.m || !parsed?.d) {
        return undefined;
    }
    const month = String(parsed.m).padStart(2, "0");
    const day = String(parsed.d).padStart(2, "0");
    return `${parsed.y}-${month}-${day}`;
}

function civilToImportStartDateTime(civilDate: string): string {
    return `${civilDate}T00:00:00`;
}

function civilToImportEndDateTime(civilDate: string): string {
    return `${civilDate}T23:59:59`;
}

function naturalKey(name: string, startDate: string): string {
    return (
        name
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, " ")
            .trim() +
        "|" +
        startDate
    );
}

function errorSetHasRowCategoryError(errorSet: Set<string>, rowNumber: number): boolean {
    const prefix = `Row ${rowNumber}:`;
    return Array.from(errorSet).some((message) => message.startsWith(prefix) && message.includes("Invalid type"));
}
